import type { ToastServiceMethods } from "primevue/toastservice"
import { onUnmounted, ref } from "vue"

import type { FetchedOrderData } from "../pages/Order/types"
import DISPATCH_FETCH_ORDER from "../queries/agent_registration/dispatchFetchOrder.gql"
import POLL_WORK_ITEM from "../queries/agent_work_item/poll.gql"
import { apolloClient } from "../queries/client"
import UPDATE_IMPORTED_PARTICIPANTS from "../queries/order/updateImportedParticipants.gql"

interface NameUpdate {
  externalId: string
  newName: string
}

export function useParticipantMutations(toast: ToastServiceMethods) {
  const isFetching = ref(false)
  const fetchError = ref<string | undefined>()
  const refreshedSnapshot = ref<FetchedOrderData | null>(null)
  const isSaving = ref(false)
  let pollInterval: ReturnType<typeof setInterval> | null = null

  const cleanup = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  }

  onUnmounted(cleanup)

  const pollOnce = async (workItemId: string, onComplete: () => void) => {
    try {
      const result = await apolloClient.query({
        query: POLL_WORK_ITEM,
        variables: { id: workItemId },
        fetchPolicy: "network-only",
      })

      const item = result.data.agentWorkItem
      if (!item) return

      if (item.status === "completed") {
        cleanup()
        isFetching.value = false
        refreshedSnapshot.value = item.result as FetchedOrderData
        onComplete()
        toast.add({
          severity: "success",
          summary: "Updated",
          detail: "Contact list refreshed",
          life: 3000,
        })
      } else if (item.status === "failed") {
        cleanup()
        isFetching.value = false
        toast.add({
          severity: "error",
          summary: "Error",
          detail: item.errorMessage || "Failed to fetch contacts",
          life: 5000,
        })
      }
    } catch (error) {
      console.error("Poll error:", error)
      cleanup()
      isFetching.value = false
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Lost connection while fetching contacts",
        life: 5000,
      })
    }
  }

  const fetchParties = async (
    agentRegistrationId: string,
    fileNumber: string,
    agentName: string,
    onComplete: () => void,
  ) => {
    cleanup()
    isFetching.value = true
    fetchError.value = undefined

    try {
      const result = await apolloClient.mutate({
        mutation: DISPATCH_FETCH_ORDER,
        variables: { agentRegistrationId, fileNumber },
      })

      if (result?.data?.dispatchFetchOrder?.errors?.length) {
        fetchError.value = result.data.dispatchFetchOrder.errors.join(", ")
        isFetching.value = false
        toast.add({
          severity: "error",
          summary: "Error",
          detail: fetchError.value,
          life: 5000,
        })
        return
      }

      const workItemId = result.data.dispatchFetchOrder.workItem.id
      cleanup()
      pollInterval = setInterval(() => {
        void pollOnce(workItemId, onComplete)
      }, 2000)
    } catch (error) {
      console.error("Dispatch error:", error)
      isFetching.value = false
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch contacts from " + agentName,
        life: 5000,
      })
    }
  }

  const performSave = async (
    orderId: string,
    selectedIds: Set<string>,
    nameUpdates: NameUpdate[],
    onSuccess: () => void,
  ) => {
    isSaving.value = true

    try {
      const variables: Record<string, unknown> = {
        orderId,
        selectedExternalIds: [...selectedIds],
      }

      if (refreshedSnapshot.value) {
        variables.rawSnapshot = refreshedSnapshot.value
      }

      if (nameUpdates.length > 0) {
        variables.nameUpdates = nameUpdates
      }

      const result = await apolloClient.mutate({
        mutation: UPDATE_IMPORTED_PARTICIPANTS,
        variables,
      })

      if (result?.data?.updateImportedParticipants?.errors?.length) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.data.updateImportedParticipants.errors.join(", "),
          life: 5000,
        })
        return
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Participants updated",
        life: 4000,
      })

      onSuccess()
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to update participants",
        life: 5000,
      })
    } finally {
      isSaving.value = false
    }
  }

  return {
    isFetching,
    fetchError,
    refreshedSnapshot,
    isSaving,
    cleanup,
    fetchParties,
    performSave,
  }
}
